import { router } from '@/app/router'
import { useGetPlan } from '@/shared/hooks/usePlannerAgent'

function RiskSeverityBadge({ severity }: { severity: 'low' | 'medium' | 'high' | 'critical' }) {
  const colors = {
    low: 'text-green-700 bg-green-100',
    medium: 'text-yellow-700 bg-yellow-100',
    high: 'text-orange-700 bg-orange-100',
    critical: 'text-red-700 bg-red-100'
  }
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium uppercase ${colors[severity]}`}>{severity}</span>
}

export function PlanCompletedModal({ planId, onClose }: { planId: string; onClose: () => void }) {
  const { data: plan, isLoading } = useGetPlan(planId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-3xl rounded-lg bg-background shadow-lg overflow-hidden flex flex-col max-h-[80vh]">
        <div className="border-b p-4">
          <h2 className="text-xl font-bold">🎉 Plan Ready</h2>
          <p className="text-sm text-text-03">Your planning session has completed.</p>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {isLoading && <div>Loading plan results...</div>}

          {plan && (
            <div className="space-y-6">
              {/* Milestones */}
              <section>
                <h3 className="font-semibold mb-3">Milestones ({plan.milestones.length})</h3>
                <div className="space-y-3">
                  {plan.milestones.map(m => (
                    <div key={m.id} className="rounded-lg border p-4 shadow-sm">
                      <p className="font-medium text-lg">{m.title}</p>
                      <p className="text-sm text-text-03 mt-1">{m.description}</p>
                      <p className="text-xs font-semibold text-theme-primary-05 mt-2">{m.tasks.length} tasks included</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Risks */}
              {plan.risks.length > 0 && (
                <section>
                  <h3 className="font-semibold mb-3">Risks ({plan.risks.length})</h3>
                  <div className="space-y-2 rounded-lg border bg-background-neutral-02/30 p-4">
                    {plan.risks.map(r => (
                      <div key={r.id} className="flex items-start gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                        <RiskSeverityBadge severity={r.severity} />
                        <span className="flex-1 leading-relaxed">{r.description}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="border-t p-4 flex justify-end gap-3 bg-background-neutral-02/20">
          <button className="rounded border px-4 py-2 text-sm font-medium hover:bg-background-neutral-02" onClick={onClose}>Close</button>
          <button className="rounded bg-theme-primary-05 px-4 py-2 text-sm font-medium text-primary-foreground" onClick={() => router.navigate(`/workplace/plans/${planId}`)}>View Full Plan</button>
        </div>
      </div>
    </div>
  )
}
